import { demoMemberMedia } from '../state/demoMedia';

export type CommunitySeedMember = { id: string; display_name: string; username: string };

/** Fictional development content. Timestamps are relative so story expiry is testable. */
export function createCommunitySeed(members: CommunitySeedMember[], currentUserId: string) {
  const others = members.filter((member) => member.id !== currentUserId);
  const member = (index: number) =>
    (others[index % Math.max(others.length, 1)] ?? members[0])?.id ?? currentUserId;
  const ago = (hours: number) => new Date(Date.now() - hours * 3_600_000).toISOString();
  const later = (hours: number) => new Date(Date.now() + hours * 3_600_000).toISOString();
  return {
    posts: members.map((person, index) => ({
      id: `demo-post-${index + 1}`,
      author_id: person.id,
      body: [
        'Ironwood Garage finally has its morning routine: a warm-up, the main lifts, then everything back on the wall. Giving each piece a home made the biggest difference. What is one layout change you would make here?',
        'Basement Barbell update: a red rack, the basic lifts, and a floor I can actually walk across. This weekend I moved the storage to the side wall so the bench can roll straight into the rack. Small-space builders: what earned its spot in your gym?',
        'Bright Denver morning, a few warm-up rows, and a dumbbell session done before work. The Sunday Studio is still a work in progress, but the spare-room dumbbell corner is finally sorted.',
        'Backyard session at Southwest Strength Shed. Cleared a proper kettlebell lane next to the squat stand and gave the plates a home against the plywood wall. The gym feels calmer, and I spend less time moving gear before a workout.',
        'Northside Lift Lab update: the fan bike has its own corner and the plate tree lives next to the wooden platform. It is a small change, but the whole room flows better. Sharing a few photos before the next project takes over the floor.',
      ][index % 5],
      created_at: ago(1 + index * 2),
      visibility: 'public' as const,
      reactions: members.filter((other) => other.id !== person.id).map((other) => other.id),
      comments: members
        .filter((other) => other.id !== person.id)
        .map((other, commentIndex) => ({
          id: `demo-post-${index + 1}-comment-${commentIndex + 1}`,
          author_id: other.id,
          body: [
            'That clear path beside the rack makes the whole space feel bigger. Great use of the wall storage.',
            'The bench has a proper home now. I might borrow that idea for my own garage.',
            'Love seeing a setup built around the sessions you actually do. The little details add up.',
            'The open floor is the best upgrade here. Plenty of room to warm up before the first set.',
          ][commentIndex],
          created_at: ago(0.25 + index * 2 + commentIndex * 0.12),
        })),
      equipment_tags: [
        ['Power rack', 'Plate storage'],
        ['Adjustable bench', 'Wall storage'],
        ['Rowing machine', 'Dumbbells'],
        ['Kettlebells', 'Bench'],
        ['Power rack', 'Fan bike'],
      ][index % 5],
      showcase: index === 1 || index === 4 ? 'storage' : 'garage',
      image: demoMemberMedia(person.id)?.cover,
    })),
    stories: members.flatMap((person, index) => {
      const media = demoMemberMedia(person.id);
      const captions = [
        [
          '6 a.m. at Ironwood Garage. Coffee first, then the rack.',
          'A clear floor and a simple plan. Ready for the next session.',
        ],
        [
          'Basement Barbell: under the joists, the session starts.',
          'Weekend win: every plate finally has a place.',
        ],
        [
          'A little morning light in The Sunday Studio.',
          'Dumbbell corner sorted. One less excuse to skip accessories.',
        ],
        [
          'Southwest Strength Shed, after work. This is my reset.',
          'Tonight: a few main lifts and a short kettlebell finisher.',
        ],
        [
          'A quick spin on the fan bike at Northside Lift Lab.',
          'Rack, plates, clear walkway. The layout is coming together.',
        ],
      ][index % 5];
      return captions.map((body, storyIndex) => ({
        id: `demo-story-${index + 1}-${storyIndex + 1}`,
        author_id: person.id,
        body,
        created_at: ago(1 + index + storyIndex * 0.5),
        expires_at: later(23 - index - storyIndex * 0.5),
        views: [] as string[],
        reactions: [] as string[],
        scene: index === 1 ? 'storage' : 'garage',
        image: storyIndex === 0 ? media?.cover : (media?.gallery[1]?.image ?? media?.cover),
      }));
    }),
    follows: members.flatMap((person) =>
      members
        .filter((other) => other.id !== person.id)
        .map((other) => ({
          follower_id: person.id,
          following_id: other.id,
          status: 'accepted' as const,
        })),
    ),
    blocks: [] as string[],
    mutes: [] as string[],
    forum_categories: [
      { id: 'builds', name: 'Gym builds & layouts', description: 'Make every square foot count.' },
      {
        id: 'equipment',
        name: 'Equipment talk',
        description: 'Honest advice from people who lift.',
      },
      {
        id: 'training',
        name: 'Training together',
        description: 'Routines, milestones, and accountability.',
      },
      { id: 'diy', name: 'DIY & maintenance', description: 'Build it, fix it, keep it working.' },
    ],
    topics: [
      {
        id: 'topic-layout',
        category_id: 'builds',
        author_id: member(1),
        title: 'What would you change in a one car garage layout?',
        body: 'I have a 10 × 20 foot space with a rack, bench, and adjustable dumbbells. The door needs to stay usable. What layout changes made the biggest difference for you?',
        created_at: ago(8),
        tags: ['Small spaces', 'Layout'],
        replies: [
          {
            id: 'reply-layout-1',
            author_id: member(0),
            body: 'Put the rack on the back wall and keep a clear path down one side. Mark the door track and operating clearances before moving anything.',
            created_at: ago(6),
          },
          {
            id: 'reply-layout-2',
            author_id: member(2),
            body: 'Wall mounted storage freed up more room than I expected. Make sure the wall and fixings are suitable for the load.',
            created_at: ago(4),
          },
        ],
        subscriber_ids: [member(1)],
        accepted_reply_id: 'reply-layout-1',
      },
      {
        id: 'topic-floor',
        category_id: 'equipment',
        author_id: member(3),
        title: 'Flooring lessons you wish you knew before your first build',
        body: 'I am researching flooring for a garage gym. What should I check before committing? Particularly interested in ventilation, leveling, and protecting the concrete.',
        created_at: ago(20),
        tags: ['Flooring', 'First build'],
        replies: [
          {
            id: 'reply-floor-1',
            author_id: member(4),
            body: 'Check the manufacturer’s intended use and care guidance. I left room for expansion and made sure the garage drain was still accessible.',
            created_at: ago(18),
          },
        ],
        subscriber_ids: [member(3)],
        accepted_reply_id: undefined,
      },
      {
        id: 'topic-first',
        category_id: 'training',
        author_id: member(4),
        title: 'The weekly check in: what did you get done?',
        body: 'Big lifts, ten minute workouts, or simply cleaning up the space. Tell us your win from this week.',
        created_at: ago(25),
        tags: ['Check in'],
        replies: [],
        subscriber_ids: [member(4)],
        accepted_reply_id: undefined,
      },
    ],
    listings: [
      {
        id: 'listing-bench',
        author_id: member(2),
        title: 'Adjustable utility bench',
        description:
          'Well cared for adjustable bench from my fictional demo gym. Looking to swap for compact plate storage. Happy to answer questions before arranging a meetup.',
        category: 'Benches',
        condition: 'Good',
        location: 'Austin, TX',
        status: 'available' as const,
        trade_for: 'Compact plate storage',
        created_at: ago(12),
        expires_at: later(24 * 30),
        asking_value: 150,
        currency: 'USD',
        scene: 'bench',
      },
      {
        id: 'listing-plates',
        author_id: member(3),
        title: 'Pair of 25 lb bumper plates',
        description:
          'A matching pair with cosmetic wear. Looking for change plates or a pair of kettlebells. Local exchange only.',
        category: 'Plates',
        condition: 'Used',
        location: 'Chicago, IL',
        status: 'available' as const,
        trade_for: 'Change plates or kettlebells',
        created_at: ago(26),
        expires_at: later(24 * 30),
        asking_value: 80,
        currency: 'USD',
        scene: 'plates',
      },
      {
        id: 'listing-storage',
        author_id: member(1),
        title: 'Vertical barbell storage',
        description: 'Freestanding barbell storage. Trading because I changed my garage layout.',
        category: 'Storage',
        condition: 'Like new',
        location: 'Denver, CO',
        status: 'reserved' as const,
        trade_for: 'Cable attachments',
        created_at: ago(48),
        expires_at: later(24 * 30),
        currency: 'USD',
        scene: 'storage',
      },
    ],
    conversations: [
      {
        id: 'conversation-welcome',
        member_ids: [currentUserId, member(0)],
        status: 'accepted' as const,
        requester_id: member(0),
        unread_count: 1,
        messages: [
          {
            id: 'message-1',
            author_id: member(0),
            body: 'Hey! Welcome to the garage. What are you working on in your gym right now?',
            created_at: ago(2),
          },
        ],
      },
      {
        id: 'conversation-request',
        member_ids: [currentUserId, member(5)],
        status: 'pending' as const,
        requester_id: member(5),
        unread_count: 1,
        messages: [
          {
            id: 'message-2',
            author_id: member(5),
            body: 'Your setup sounds great. Mind if I ask about your storage layout?',
            created_at: ago(5),
          },
        ],
      },
    ],
    notifications: [
      {
        id: 'notification-comment',
        type: 'comments',
        title: 'A new perspective on your gym',
        body: 'Your community is sharing ideas. Join the conversation in the feed.',
        created_at: ago(1),
        read: false,
        path: '/home',
      },
      {
        id: 'notification-message',
        type: 'messages',
        title: 'You have a message',
        body: 'A fellow garage gym builder sent you a welcome.',
        created_at: ago(2),
        read: false,
        path: '/messages',
      },
      {
        id: 'notification-forum',
        type: 'forum',
        title: 'A layout question worth a look',
        body: 'Members are sharing ideas for a one car garage.',
        created_at: ago(4),
        read: true,
        path: '/forum/topic-layout',
      },
      {
        id: 'notification-trade',
        type: 'trade',
        title: 'Fresh equipment trades',
        body: 'Discover local equipment swaps from the community.',
        created_at: ago(12),
        read: true,
        path: '/trade',
      },
    ],
    notification_preferences: {
      follows: true,
      reactions: true,
      comments: true,
      messages: true,
      forum: true,
      trade: true,
      activity_email: false,
    },
  };
}
